import React, { useEffect, useState, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { useMutation, useQuery } from "@apollo/client";
import { ProfileContext } from "../../../App";
import { GET_CANDIDATES } from "../../../GraphQL/candidates/queries";
import { ADD_JOB_MATCH } from "../../../GraphQL/matches/mutations";
import { CREATE_JOB_TOPIC } from "../../../GraphQL/messages/mutations";
import { Candidate, Community, PublicJob } from "../../../interfaces";
import { currencyFormat } from "../../../utils";
import { getCommunityLogo } from "../../../utils/communityLogos";
import confirmWithModal from "../../Global/confirmWithModal";
import parse from "html-react-parser";
import ReactSelect from "react-select";
import {
  ButtonRightCorner,
  PartnerBadge,
  PartnerLogo,
} from "../../../sharedStyles";
import ReadOnlyAttachmentList from "../../Attachments/ReadOnlyAttachmentList";

interface Props {
  show: boolean;
  onHide: () => void;
  role: PublicJob;
  hideMatchingSection?: boolean; // Hide matching sections when opened from match context
}

const PublicRoleDetailModal = ({ show, onHide, role, hideMatchingSection = false }: Props) => {
  const { userProfile } = useContext(ProfileContext);

const { loading, error, data } = useQuery(GET_CANDIDATES, {
    variables: { skip: 0, take: 25, order: { updatedAt: "DESC" } }
  });

  const [addCandidateMatch] = useMutation(ADD_JOB_MATCH);
  const [createTopic] = useMutation(CREATE_JOB_TOPIC);

  const [query, setQuery] = useState<string>("");
  const [showQuery, setShowQuery] = useState<boolean>(false);
  const [multipleCandidates, setMultipleCandidates] = useState<any[]>([]);
  const [candidateIds, setCandidateIds] = useState<number[]>([]);

  useEffect(() => {
    let data: number[] = [];
    for (let i in multipleCandidates)
      data.push(multipleCandidates[i].value as number);
    setCandidateIds(data);
  }, [multipleCandidates]);

  if (!role || loading || error) return null;

  const candidateOptions = data.candidates?.items?.filter(
    (candidate: Candidate) => candidate.status === "SHARED"
  );
  // TODO: Filter roles so only opted in are included

  let proposedCandidateValue = [];
  if (candidateOptions) {
    for (let i in candidateOptions) {
      const value = candidateOptions[i]?.id;
      const label = `${candidateOptions[i]?.firstName} ${candidateOptions[
        i
      ]?.lastName?.slice(0, 1)}. - ${candidateOptions[i].idealJobTitles}`;
      proposedCandidateValue.push({ value, label });
    }
  }

  const handleClick = async () => {
    if (!candidateIds.length)
      return alert("Please select a role to put forward for this candidate");

    const confirmed = await confirmWithModal(
      <>
        <p>Before sharing a candidate, you confirm that</p>
        <ul>
          <li>
            You have received permission from the Candidate to be posted on the
            platform;
          </li>
          <li>
            there is a genuine opportunity for the Candidate to accept a role
            with a Client; and
          </li>
          <li>
            your agency has interviewed or screened the Candidate within the
            last three months.
          </li>
        </ul>
      </>
    );
    if (!confirmed) return;

    try {
      await addCandidateMatch({
        variables: { jobId: role.id, candidateIds: candidateIds },
      });
    } catch (err) {
      return alert(
        "Something went wrong, have you already put forward this candidate for this role? If not please try again soon"
      );
    }
    alert("Candidate has been put forward for this role!");
    onHide();
  };

  const handleSendQuery = async () => {
    try {
      await createTopic({
        variables: {
          jobId: role?.id,
          message: query,
        },
      });
      alert("Your query has been sent to the client agent");
      setShowQuery(false);
    } catch (error) {
      alert("Something went wrong, please try again soon");
      setShowQuery(false);
    }
  };

  const renderHeader = () => {
    if (!userProfile) return null;

    const communities = userProfile.communities.filter((c: Community) => {
      return role.communities?.findIndex((rc) => rc.id === c.id) > -1;
    });

    return (
      <DialogHeader>
        <div className="flex justify-between items-start">
          <div>
            <DialogTitle className="text-2xl">{role.title}</DialogTitle>
            <div className="mt-2">{role.location.displayName}</div>
            <div className="mt-1">
              Fee Estimate:{" "}
              <strong>
                {currencyFormat(role.feeMin)} - {currencyFormat(role.feeMax)}{" "}
                {role?.currency}
              </strong>
              {role.workType === "TEMP" && " per hr"}
            </div>
          </div>
          <PartnerBadge>
            {communities.length > 0 && (
              <>
                <PartnerLogo
                  src={getCommunityLogo(communities[0].name, communities[0].logoUrl)}
                  width={64}
                />
                <br />
                <div>
                  Member of
                  <br />
                  {communities[0].name}
                </div>
              </>
            )}
          </PartnerBadge>
        </div>
      </DialogHeader>
    );
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {renderHeader()}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <strong>Work type:</strong>{" "}
              {role.workType === "TEMP" ? "CONTRACT/TEMP" : "PERMANENT"}
              <br />
              <strong>Industry:</strong> {role.industry}
              <br />
              {role.workType === "PERMANENT" && (
                <>
                  <strong>Salary:</strong> {currencyFormat(role.salaryMin)} -{" "}
                  {currencyFormat(role.salaryMax)} {role?.currency}
                </>
              )}
              {role.workType === "TEMP" && (
                <>
                  <strong>Rate:</strong> {currencyFormat(role.salaryMin)} -{" "}
                  {currencyFormat(role.salaryMax)} per hr
                </>
              )}
              <br />
              {role.workType === "PERMANENT" && (
                <>
                  <strong>Start date:</strong>{" "}
                  {new Date(role.startDate).toLocaleDateString()}
                  <br />
                </>
              )}
              {role.workType === "TEMP" && (
                <>
                  <strong>Placement start date:</strong>{" "}
                  {new Date(role.placementStartDate).toLocaleDateString()}
                  <br />
                </>
              )}
              {role.workType === "TEMP" && (
                <>
                  <strong>Placement end date:</strong>{" "}
                  {new Date(role.placementEndDate).toLocaleDateString()}
                  <br />
                </>
              )}
            </div>
            <div>
              {role.workType === "TEMP" && (
                <>
                  <strong>Working hours:</strong> {role.workingHours}
                  <br />
                </>
              )}
              <strong>Engagement type:</strong> {role.engagementType}
              <br />
              <strong>Terms signed:</strong> {role.termsSigned ? "Yes" : "No"}
              <br />
              <strong>Replacement guarantee:</strong>{" "}
              {role.replacementGuarantee ? "Yes" : "No"}
              <br />
            </div>
          </div>

          <hr />

          <div>
            {parse(role?.summary.split("::")[0])}
            <br />
            <a href={role?.summary.split("::")[1]} target="_blank" rel="noreferrer">
              {role?.summary.split("::")[1]}
            </a>
          </div>

          <ReadOnlyAttachmentList entityId={role.id} type="job" />

          {showQuery && (
            <>
              <hr />
              <div className="space-y-2">
                <Textarea
                  name="description"
                  rows={5}
                  placeholder="Enter query ..."
                  onChange={(e) => setQuery(e.target.value)}
                  required
                />
              </div>
              <p className="my-3">
                Queries will be anonymous. No information to identify you or your
                agency will be exchanged when you submit a query.
              </p>
              <ButtonRightCorner>
                <Button
                  className="mx-1"
                  variant="secondary"
                  onClick={() => setShowQuery(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="mx-1"
                  variant="default"
                  onClick={handleSendQuery}
                >
                  Send
                </Button>
              </ButtonRightCorner>
            </>
          )}

          {!hideMatchingSection && (
            <>
              <hr />
              <h5 className="text-lg font-semibold">Propose a matching candidates</h5>
              <div className="space-y-2">
                <Label htmlFor="proposedCandidate">
                  Select a candidates you would like to put forward for this role:
                </Label>
                <ReactSelect
                  onChange={(data: any) => setMultipleCandidates(data)}
                  name="proposedCandidate"
                  id="proposedCandidate"
                  isMulti={true}
                  options={proposedCandidateValue}
                />
              </div>
            </>
          )}

          <div>
            {!showQuery && (
              <Button onClick={() => setShowQuery(true)} size="sm">
                <Mail className="mr-2 h-4 w-4" /> Agent Query
              </Button>
            )}
          </div>

          {!hideMatchingSection && (
            <>
              <hr />
              <div>
                <p>
                  <strong>What happens next?</strong>
                </p>
                <p>
                  The Client Agency that has listed this role will receive the anonymous
                  details of your candidate along with your agency name. If they choose
                  to proceed, you'll receive details of their agency to confirm whether
                  you'd like to proceed. If they decline, we'll let you know.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
          {!hideMatchingSection && <Button onClick={handleClick}>Submit</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublicRoleDetailModal;
